// i18n: translated
/**
 * サイドバーナビゲーション
 *
 * MOCK v2.7 準拠: セクション分けグループ + 管理アコーディオン。
 * RBAC 権限に基づいてメニュー項目を表示/非表示。
 * 未有効化の機能はグレーアウト表示 (管理者設定で有効化)。
 * SPEC 7.1 ページ一覧 + SPEC 5.2 RBAC 権限モデル準拠。
 */
import { useState, useCallback, useMemo } from 'react';
import { preloadNavigationPage } from '@/pageLoaders';
import { useLocation, useNavigate } from 'react-router-dom';
import { useT } from '@/i18n';
import type { shellJa } from '@/i18n/locales/ja/shell';
import { shellNs } from '@/i18n/namespaces/shell';
import { usePermission } from '@/hooks/usePermission';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { canAccessNavigationItem } from '@/utils/navigationAccess';
import type { PermissionArea, PermissionAction } from '@/types';
// PR-30 / #270: Chatwork / LINE は外部サービスの識別性を上げるため公式アイコンを使う。
// 他メニューは引き続き Heroicons の SVG path を使う (汎用 UI 用)。
// Chatwork は light / dark テーマで brand カラーの dark navy ブロック (`#13202f`) が
// 見えにくくなるため、`color` (light bg 用) と `color_invert` (dark bg 用) を
// Tailwind の `block dark:hidden` で切り替えて配置する。
import chatworkColorLight from '@/assets/logos/chatwork-color.svg';
import chatworkColorDark from '@/assets/logos/chatwork-color-invert.svg';
import lineBrandIcon from '@/assets/logos/line-brand-icon.png';
// PR-32 (#272): 内部機能 (Dashboard / Clients / Cases / ... / Admin) のアイコンを
// AceCollect 専用デザインの SVG セットに差し替える。`?raw` で SVG 文字列を取り込み、
// `stroke="currentColor"` でテーマ色 (text-gray-* / text-brand-*) を継承させる。
// Chatwork / LINE は PR-30 公式ロゴ img を維持 (本 PR では touch しない)。
import dashboardSvg from '@/assets/icons/nav/dashboard.svg?raw';
import clientsSvg from '@/assets/icons/nav/clients.svg?raw';
import clientGroupsSvg from '@/assets/icons/nav/client-groups.svg?raw';
import clientTasksSvg from '@/assets/icons/nav/client-tasks.svg?raw';
import casesSvg from '@/assets/icons/nav/cases.svg?raw';
import calendarSvg from '@/assets/icons/nav/calendar.svg?raw';
import collectionsSvg from '@/assets/icons/nav/collections.svg?raw';
import templatesSvg from '@/assets/icons/nav/templates.svg?raw';
import documentMasterSvg from '@/assets/icons/nav/document-master.svg?raw';
import creditorMasterSvg from '@/assets/icons/nav/creditor-master.svg?raw';
import vaultSvg from '@/assets/icons/nav/vault.svg?raw';
import reportsSvg from '@/assets/icons/nav/reports.svg?raw';
import messageSettingsSvg from '@/assets/icons/nav/message-settings.svg?raw';
import settingsSvg from '@/assets/icons/nav/settings.svg?raw';
import adminSvg from '@/assets/icons/nav/admin.svg?raw';
// 管理配下 3 項目 (組織設定 / ユーザー管理 / 監査) を識別しやすくする専用アイコン (旧: 全て adminSvg の同一盾)。
// User/Mgr 提供 PNG (icon_org_settings / icon_user_management / icon_operations) の意匠を参考に、
// 既存 nav icon と同一方式 (monochrome / stroke=currentColor / color 属性は現 admin.svg と同じ #6d4ad6) の
// SVG を新規作成。色は 3 項目で統一し「形状」で差別化する (盾は使わない)。PNG は repo に取り込まない (意匠参考のみ)。
// admin-settings-information-architecture: 3 項目目を「運用 (レンチ)」から「監査 (書類 + チェック)」へ差し替え。
// 旧 admin-operations.svg は他画面から参照されなくなるが、履歴として asset は残す。
import adminUsersSvg from '@/assets/icons/nav/admin-users.svg?raw';
import adminAuditSvg from '@/assets/icons/nav/admin-audit.svg?raw';

// ==============================
// メニュー定義
// ==============================

/** 表示言語別の項目名のキー (`i18n/locales/{ja,en}/shell.ts` の `nav.items`)。 */
type NavItemKey = keyof typeof shellJa.nav.items;
/** 表示言語別のセクション見出しのキー (`nav.sections`)。 */
type NavSectionKey = keyof typeof shellJa.nav.sections;

interface MenuItem {
  /**
   * メニューラベル (日本語 = 正本)。画面の表示は `labelKey` で表示言語別に引く。
   * 日本語の辞書と同じ文字列であることは `i18n/navLabels.test.ts` が保証する。
   */
  label: string;
  /** 表示言語別の項目名のキー (admin-english-toggle-20260924 / ADR-081) */
  labelKey: NavItemKey;
  /** ルートパス */
  path: string;
  /**
   * #406 UI-P2: true のとき active 判定を完全一致 (pathname === path) のみとする。
   * path が別 item の path の prefix になる場合 (例: 明細取込 `/payments/imports` は
   * 取込履歴 `/payments/imports/history` の prefix) に、prefix 一致で両方が同時 active に
   * なる二重 active を防ぐ。未指定は従来どおり prefix 一致 (startsWith)。
   */
  exact?: boolean;
  /** Heroicons アイコン SVG パスデータ (公式アイコン使用時は省略可) */
  iconPath?: string;
  /**
   * PR-30 (#270): Heroicons の代わりに公式アイコンを使う場合の img src。
   * light / dark で切替が必要な場合は `{ light, dark }` を渡す。
   */
  iconImage?: { light: string; dark?: string };
  /**
   * PR-32 (#272): AceCollect 専用 SVG (Vite `?raw` 文字列) を inline 描画。
   * `stroke="currentColor"` を使った monochrome SVG 前提で、Tailwind の text-color
   * 経由でテーマ色 (active / hover / 通常 / Beta グレー) を制御する。
   */
  iconSvg?: string;
  /**
   * PR-34 (#274): viewBox 内描画 bounds / stroke 密度の違いで生まれる
   * optical size の不揃いを補正する Tailwind scale class (例: `scale-95`,
   * `scale-110`)。layout box (h-5 w-5) は維持したまま CSS transform で
   * 視覚サイズだけ調整する。未指定で 100%。
   */
  iconScale?: string;
  /**
   * PR-34 (#274): Sidebar 内での見た目を抑える opacity class (例: `opacity-95`)。
   * 公式 brand 画像 (Chatwork / LINE) が internal stroke icon に比べて
   * 強く見えすぎる場合に控えめに馴染ませる用途。未指定で 100%。
   * Beta グレーアウト時は `opacity-40` が優先される。
   */
  iconOpacity?: string;
  /** RBAC 権限エリア (未指定 = 全ロール表示) */
  permission?: PermissionArea;
  /**
   * #406 Slice 3: `permission` エリア内で必要なアクション。複数指定は OR 判定
   * (どれか 1 つ持てば表示)。未指定は従来どおり `read` 判定。
   */
  permissionActions?: PermissionAction[];
  /**
   * steering 463: 複数 permission area の **OR** で表示可否を決める項目用。
   * `permission` が単一 area しか取れないため追加した。指定時は `permission` より優先し、
   * どれか 1 つでも read できれば表示する (組織設定の入口条件を sidebar でも表現する)。
   */
  permissionAnyArea?: PermissionArea[];
  /** 複数領域の閲覧が必要な画面 (ダッシュボードの案件集計など)。 */
  permissionAllAreas?: PermissionArea[];
  /** 個人用導線で表示対象を広げても、既定の業務入口は従来の閲覧権限で選ぶ。 */
  homePermissionAnyArea?: PermissionArea[];
  /** Beta 機能フラグ名 */
  betaFeature?: 'chatwork' | 'line';
  /** サブメニュー (管理アコーディオン用) */
  children?: SubMenuItem[];
}

interface SubMenuItem {
  /** サブメニューラベル (日本語 = 正本) */
  label: string;
  /** 表示言語別の項目名のキー (未指定は label をそのまま出す) */
  labelKey?: NavItemKey;
  /** ルートパス */
  path: string;
}

/**
 * セクションのアクセント種別 (sidebar-navigation-redesign)。
 * 未指定は `brand` (既存の indigo)。適用面は **セクション見出し / ミニレール /
 * アイコン / active 行** に限定する
 * (通常時のラベル文字色は他セクションと同じに保ち、過剰装飾にしない)。
 *
 * sidebar-color-system-finalization: 正本画像のカテゴリ別カラー体系へ合わせ、
 * 「入金管理」だけだった識別色を全カテゴリへ広げる。
 * `brand` はダッシュボード (見出しなし) と「コミュニケーション」(neutral 見出し +
 * Chatwork / LINE の brand カラー維持) が引き続き使う。
 */
type SectionAccent = 'brand' | 'blue' | 'violet' | 'emerald' | 'indigo' | 'slate';

interface MenuSection {
  /** セクションヘッダーラベル (null = ヘッダーなし)。日本語 = 正本 */
  header: string | null;
  /** 表示言語別の見出しのキー (header が null のときは null) */
  headerKey: NavSectionKey | null;
  /**
   * セクションのアクセント (未指定 = 'brand')。
   * 色トークンの実体は SECTION_ACCENTS。
   */
  accent?: SectionAccent;
  /** セクション内のメニュー項目 */
  items: MenuItem[];
}

/**
 * アクセント別の Tailwind クラストークン (sidebar-navigation-redesign)。
 *
 * index.css に `.bg-brand-500\/10` / `.text-brand-400` の全体 override (`!important`) が
 * あるため、brand 以外は brand クラスを再利用せず独立したクラスで表現する
 * (再利用すると indigo に上書きされる)。index.css には新しい override を追加しない。
 * override の対象は brand 系と gray 系だけなので、sky / violet / indigo / slate の
 * ユーティリティはそのまま効く (確認済み)。
 *
 * sidebar-color-system-finalization: 各アクセントは emerald と同じトークン構成
 * (rail = bg-X-500 / header = text-X-600 dark:text-X-400 / activeRow = bg-X-500/10 +
 * text-X-700 dark:text-X-300 / indicator = bg-X-500) に揃える。light / dark どちらの
 * 面でも読めるトーンを選ぶため、実体は Tailwind の 500〜700 帯を使う。
 */
const SECTION_ACCENTS: Record<SectionAccent, Record<string, string>> = {
  brand: {
    /** 見出し左のミニレール (グループの起点を示す) */
    rail: 'bg-gray-300 dark:bg-gray-600',
    /** 見出しテキスト */
    header: 'text-gray-500 dark:text-gray-400',
    /** active 行の背景 + ラベル色 */
    activeRow: 'bg-brand-500/10 text-brand-400',
    /** active 行の左インジケーター */
    indicator: 'bg-brand-500',
    /** アイコン (非 active) — iconPath 系のみ適用 (iconSvg は designer カラーを保持) */
    iconIdle:
      'text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200',
    /** アイコン (active) */
    iconActive: 'text-brand-400',
  },
  /** 案件・クライアント (アイコン実体は各 SVG の color="#1c8fd6") */
  blue: {
    rail: 'bg-sky-500',
    header: 'text-sky-600 dark:text-sky-400',
    activeRow: 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
    indicator: 'bg-sky-500',
    iconIdle:
      'text-sky-600/70 group-hover:text-sky-600 dark:text-sky-400/70 dark:group-hover:text-sky-300',
    iconActive: 'text-sky-600 dark:text-sky-300',
  },
  /** 書類・資料 (アイコン実体は各 SVG の color="#8b5cf6") */
  violet: {
    rail: 'bg-violet-500',
    header: 'text-violet-600 dark:text-violet-400',
    activeRow: 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
    indicator: 'bg-violet-500',
    iconIdle:
      'text-violet-600/70 group-hover:text-violet-600 dark:text-violet-400/70 dark:group-hover:text-violet-300',
    iconActive: 'text-violet-600 dark:text-violet-300',
  },
  /** 入金管理 (アイコンは iconPath 系のため下記クラスがそのまま効く) */
  emerald: {
    rail: 'bg-emerald-500',
    header: 'text-emerald-600 dark:text-emerald-400',
    activeRow: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    indicator: 'bg-emerald-500',
    iconIdle:
      'text-emerald-600/70 group-hover:text-emerald-600 dark:text-emerald-400/70 dark:group-hover:text-emerald-300',
    iconActive: 'text-emerald-600 dark:text-emerald-300',
  },
  /** 設定 (アイコン実体は各 SVG の color="#5a6ef0"。書類・資料の violet とは別色) */
  indigo: {
    rail: 'bg-indigo-500',
    header: 'text-indigo-600 dark:text-indigo-400',
    activeRow: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
    indicator: 'bg-indigo-500',
    iconIdle:
      'text-indigo-600/70 group-hover:text-indigo-600 dark:text-indigo-400/70 dark:group-hover:text-indigo-300',
    iconActive: 'text-indigo-600 dark:text-indigo-300',
  },
  /** 管理 (アイコン実体は各 SVG の color="#6f849e"。彩度を落とした cool な blue-gray) */
  slate: {
    rail: 'bg-slate-500',
    header: 'text-slate-600 dark:text-slate-400',
    activeRow: 'bg-slate-500/10 text-slate-700 dark:text-slate-300',
    indicator: 'bg-slate-500',
    iconIdle:
      'text-slate-600/70 group-hover:text-slate-600 dark:text-slate-400/70 dark:group-hover:text-slate-300',
    iconActive: 'text-slate-600 dark:text-slate-300',
  },
};

/**
 * ops surface 判定 (ops v0.1)。
 * App.tsx の detectSurfaceMode と同一規則 (App → AppLayout → Sidebar の import 循環を
 * 避けるため判定式をローカルに持つ。変更時は両方を同期すること)。
 */
const IS_OPS_SURFACE = /^(dev-)?ops\./.test(window.location.hostname);

/**
 * お知らせバナー管理 (steering 488) の nav アイコン。
 * 既存 nav SVG と同形式 (viewBox 0 0 24 24 / fill none / stroke currentColor /
 * stroke-width 2) の inline 文字列。Heroicons megaphone 系 (絵文字・記号は使わない §4.1)。
 */
const announcementSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.34 15.84A3 3 0 0 1 7.5 18H6a3 3 0 0 1 0-6h1.5a3 3 0 0 1 2.84 2.16"/><path d="M10.5 12 19 6.5v11L10.5 12Z"/><path d="M8.5 18.5 10 22"/></svg>`;

// Super Admin 管理の nav アイコン (Heroicons shield-check 系)。既存 nav SVG と同形式
// (viewBox 0 0 24 24 / fill none / stroke currentColor / stroke-width 2) の inline 文字列。
// 「全テナントを横断する強い権限」を示すため鍵や歯車ではなく盾を使う。
const superAdminSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 4 6v6c0 4.4 3.4 8.3 8 9 4.6-.7 8-4.6 8-9V6l-8-3Z"/><path d="m9 12 2 2 4-4"/></svg>`;

/**
 * Platform Operator Console (ops surface) 用メニュー (ops v0.1)。
 * tenant 画面 route は ops surface では未登録のため表示しない。
 */
export const OPS_MENU_SECTIONS: MenuSection[] = [
  {
    header: 'Platform',
    headerKey: 'platform',
    items: [
      {
        label: 'プラットフォーム管理',
        labelKey: 'platform',
        path: '/platform',
        iconSvg: adminSvg,
      },
      {
        label: 'Super Admin管理',
        labelKey: 'superAdmins',
        path: '/platform/admins',
        iconSvg: superAdminSvg,
      },
      {
        label: 'お知らせバナー',
        labelKey: 'announcements',
        path: '/platform/announcements',
        iconSvg: announcementSvg,
      },
    ],
  },
];

/**
 * 家計表管理 (#293) 用の nav アイコン。専用 asset を増やさず、既存 nav SVG と同形式
 * (viewBox 0 0 24 24 / fill none / stroke currentColor / stroke-width 2 / カテゴリ color) の
 * inline 文字列で定義する。家計表 = 表 を表す table-cells 系 (絵文字・テキスト記号は使わない §4.1)。
 * color は「案件・クライアント」カテゴリ色。
 *
 * HouseholdBudgetsPage.tsx のページ見出し (PageTitle の icon) も**この定数を import して**
 * 再利用する。literal を複製すると Sidebar とページ見出しで図柄・色が割れるため、
 * 複製せず必ずここから import すること (他 19 件の nav icon は `assets/icons/nav/*.svg` の
 * `?raw` import。本 icon だけ専用 asset を持たないため export で共有する)。
 */
export const householdBudgetsSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" color="#1c8fd6"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9.5h18"/><path d="M3 14.5h18"/><path d="M9.5 9.5V20"/></svg>`;

/** #406 明細取込の nav アイコン (Heroicons banknotes)。入金管理セクション新設前から同一 path。 */
const PAYMENTS_IMPORT_ICON_PATH =
  'M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z';

/**
 * セクション分けメニュー定義 (情報設計再編、feature/admin-menu-ia-cleanup)
 *
 * 採用構成 (route は全て据置 / App.tsx 不変 / bookmark・deep link 影響なし):
 *   - 旧「クライアント」「案件管理」を「案件・クライアント」へ統合 (家計表管理を案件一覧の近くへ)
 *   - 「書類・資料」は日常系 (書類回収 / 資料庫 / レポート) を前、設定系 (テンプレート / 書類マスタ) を後ろへ並べ替え
 *   - 「設定」セクションの項目ラベルを「外部連携・マスタ管理」とし、
 *     旧構成の「設定」セクション見出し × 「設定」項目 の二重を解消
 *   - 「管理」を「設定」配下のアコーディオンから独立セクションへ昇格 (組織設定 / ユーザー管理 / 運用 をフラット表示)
 *
 * admin-settings-information-architecture (本改修) の追加変更:
 *   - 「管理」3 項目目を「運用」(/admin/operations) から「監査」(/admin/audit) へ変更。
 *     旧「運用」の通知設定・セキュリティは「組織設定」のタブへ統合し、監査ログは「監査」の主画面にした。
 *     App.tsx の route は `admin/*` wildcard のため不変 (旧 URL は AdminPage 側で replace リダイレクト)。
 *
 * sidebar-navigation-redesign (本改修) の追加変更:
 *   - 「メッセージ設定」(/messages) を「コミュニケーション」→「設定」へ移動し、
 *     「設定」を メッセージ設定 → 外部連携・マスタ管理 の 2 項目にする。
 *     「コミュニケーション」は Chatwork / LINE / メッセージ対応 の 3 項目 (日常運用の導線) に純化する。
 *   - 「入金管理」に emerald アクセント (`accent`) を付け、入金まわりを見つけやすくする。
 *   - route は全て据置 (App.tsx 不変 / bookmark・deep link 影響なし)。
 *
 * settings-three-tab-modern-redesign (本改修) の追加変更:
 *   - /settings が 外部連携 / マスタ管理 / 運用設定 の 3 タブになり、旧ラベル
 *     「外部連携・マスタ管理」が内容と不一致になったため、項目名を「設定」へ戻す。
 *     route / icon / permission / セクション構成 / active 判定は不変。
 *
 * message-handling-navigation-move (本改修) の追加変更:
 *   - 「メッセージ対応」(/message-workspace) を「コミュニケーション」→「案件・クライアント」
 *     の末尾へ移動。日常運用で使う依頼者との会話画面が、外部連絡ツール (Chatwork / LINE) の
 *     並びに埋もれて見つけにくかったため、案件・依頼者の業務導線側へ入口を移す。
 *     「コミュニケーション」は Chatwork / LINE の 2 項目 (外部サービス連携) になる。
 *   - route / permission / 遷移先画面は不変 (App.tsx 不変 / bookmark・deep link 影響なし)。
 *     セクション移動に伴い accent は brand → blue を継承する (項目個別の色指定は追加しない)。
 */
export const MENU_SECTIONS: MenuSection[] = [
  {
    header: null,
    headerKey: null,
    items: [
      {
        label: 'ダッシュボード',
        labelKey: 'dashboard',
        path: '/',
        permission: 'dashboard',
        permissionAllAreas: ['collections'],
        // PR-32 (#272): AceCollect 専用 SVG (dashboard.svg)
        iconSvg: dashboardSvg,
        // PR-34 (#274): 3 rect grid が視覚的に重いため微縮小 (Mgr prescription)
        iconScale: 'scale-95',
      },
    ],
  },
  {
    // 旧「クライアント」+「案件管理」を統合。クライアント → 案件 → 家計表 の業務導線を近接させる。
    header: '案件・クライアント',
    headerKey: 'caseClient',
    // sidebar-color-system-finalization: 正本画像に合わせ blue で統一。
    accent: 'blue',
    items: [
      {
        label: 'クライアント一覧',
        labelKey: 'clients',
        path: '/clients',
        // PR-32 (#272): AceCollect 専用 SVG (clients.svg)
        iconSvg: clientsSvg,
        // PR-34 (#274): 3 人物 stroke が thin / bbox 20×13.9 で密度低めなため拡大
        iconScale: 'scale-110',
        permission: 'clients',
      },
      {
        label: 'クライアントグループ',
        labelKey: 'clientGroups',
        path: '/client-groups',
        // PR-32 (#272): AceCollect 専用 SVG (client-groups.svg)
        iconSvg: clientGroupsSvg,
        // PR-34 (#274): bbox 16.4×14 で全 SVG の中で最も小さく見えるため拡大
        iconScale: 'scale-110',
        permission: 'clientGroups',
      },
      {
        // steering 577: 社内タスク管理。旧「相談タスク」(steering 504) を一般化した。
        //
        // **権限を `clients` から専用 area `tasks` へ移した**のが要点。`clients` は
        // member / viewer が `none()` なので、流用したままだと自分のタスクすら
        // 見えない (この initiative が解こうとした問題そのもの)。
        //
        // route は `/client-tasks` のまま変えない (既存 bookmark / deep link を壊さない)。
        // steering 615: ラベルから「（β版）」を外した。β は画面見出しの badge に一本化。
        label: 'タスク管理',
        labelKey: 'tasks',
        path: '/client-tasks',
        iconSvg: clientTasksSvg,
        permission: 'tasks',
      },
      {
        label: '案件一覧',
        labelKey: 'cases',
        path: '/cases',
        // PR-32 (#272): AceCollect 専用 SVG (cases.svg)
        iconSvg: casesSvg,
        permission: 'collections',
      },
      {
        // #293: 家計表管理 専用ページ (案件横断 read-only 一覧)。案件従属導線として案件一覧の近くへ配置
        label: '家計表管理',
        labelKey: 'householdBudgets',
        path: '/household-budgets',
        iconSvg: householdBudgetsSvg,
        permission: 'collections',
      },
      {
        label: 'カレンダー',
        labelKey: 'calendar',
        path: '/calendar',
        // PR-32 (#272): AceCollect 専用 SVG (calendar.svg)
        iconSvg: calendarSvg,
        // PR-34 (#274): rect + 5 dots で box-filling、視覚的に重いため微縮小
        iconScale: 'scale-95',
        permission: 'collections',
      },
      {
        // steering 444: 横長モニター向けメッセージ対応 workspace (3 ペイン)。
        // 「メッセージ対応」= 日常運用の会話画面 / 「メッセージ設定」= 文面テンプレート で用途を分ける。
        // アイコンは Heroicons chat-bubble-left-right (message-settings.svg と形状で識別)。
        // message-handling-navigation-move: 日常運用の導線として「コミュニケーション」
        // (Chatwork / LINE = 外部連絡ツール) から本セクションの末尾へ移動。
        // route / permission / 画面は不変 (導線の位置だけを変える)。
        label: 'メッセージ対応',
        labelKey: 'messageWorkspace',
        path: '/message-workspace',
        iconPath:
          'M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155',
        permission: 'collections',
      },
    ],
  },
  {
    // 日常系 (書類回収 / 資料庫 / レポート) を前、設定系 (テンプレート / 書類マスタ) を後ろへ
    header: '書類・資料',
    headerKey: 'documents',
    // sidebar-color-system-finalization: 正本画像に合わせ violet で統一 (設定の indigo とは別色)。
    accent: 'violet',
    items: [
      {
        label: '書類回収',
        labelKey: 'collections',
        path: '/collections',
        // PR-32 (#272): AceCollect 専用 SVG (collections.svg)
        iconSvg: collectionsSvg,
        permission: 'collections',
      },
      {
        label: '資料庫',
        labelKey: 'vault',
        path: '/vault',
        // PR-32 (#272): AceCollect 専用 SVG (vault.svg)
        iconSvg: vaultSvg,
        // PR-34 (#274): 上段 top stripe + 本体 box で密度高め、微縮小
        iconScale: 'scale-95',
        permission: 'vault',
      },
      {
        label: 'レポート',
        labelKey: 'reports',
        path: '/reports',
        // PR-32 (#272): AceCollect 専用 SVG (reports.svg)
        iconSvg: reportsSvg,
        permission: 'reports',
      },
      {
        label: 'テンプレート',
        labelKey: 'templates',
        path: '/templates',
        // PR-32 (#272): AceCollect 専用 SVG (templates.svg)
        iconSvg: templatesSvg,
        permission: 'collections',
      },
    ],
  },
  {
    // #406 Slice 3: 入金管理 (銀行 CSV 取込 → 入金照合)。MOCK v5.8 と同一構成。
    // 銀行担当 (bank_operator) は payments のみ持つため、このセクションだけが表示される。
    header: '入金管理',
    headerKey: 'payments',
    // sidebar-navigation-redesign: 入金まわりの視認性を上げるため、唯一 emerald アクセントを付ける。
    // 適用面は見出し / アイコン / active 行のみ (通常時のラベルは他セクションと同色)。
    accent: 'emerald',
    items: [
      {
        label: '明細取込',
        labelKey: 'paymentImports',
        path: '/payments/imports',
        // #406 UI-P2: /payments/imports は取込履歴 /payments/imports/history の prefix。
        // exact 判定にして、取込履歴・その詳細ページで 明細取込 が同時 active にならないようにする。
        exact: true,
        iconPath: PAYMENTS_IMPORT_ICON_PATH,
        permission: 'payments',
        permissionActions: ['execute'],
      },
      {
        label: '取込履歴',
        labelKey: 'paymentImportHistory',
        path: '/payments/imports/history',
        iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        permission: 'payments',
        permissionActions: ['read'],
      },
      {
        label: '入金照合',
        labelKey: 'paymentMatches',
        path: '/payments/matches',
        iconPath:
          'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
        permission: 'payments',
        permissionActions: ['read'],
      },
      {
        // steering 460 gap-C: 期日を過ぎても入金が確認できない依頼者の一覧。
        label: '未入金者',
        labelKey: 'delinquency',
        path: '/payments/delinquency',
        iconPath:
          'M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z',
        permission: 'payments',
        permissionActions: ['read'],
      },
      {
        label: '仮想口座',
        labelKey: 'virtualAccounts',
        path: '/payments/virtual-accounts',
        iconPath:
          'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m3-4h10a2 2 0 012 2v6a2 2 0 01-2 2H10a2 2 0 01-2-2v-6a2 2 0 012-2zm7 5a2 2 0 11-4 0 2 2 0 014 0z',
        permission: 'payments',
        // 一覧・ルート・API と同じ read 判定。
        permissionActions: ['read'],
      },
      {
        label: '受入口座',
        labelKey: 'receivingAccounts',
        path: '/payments/receiving-accounts',
        iconPath: 'M4 10h16M4 10l8-6 8 6M6 10v8m4-8v8m4-8v8m4-8v8M4 20h16',
        permission: 'payments',
        permissionActions: ['write'],
      },
    ],
  },
  {
    header: 'コミュニケーション',
    headerKey: 'communication',
    items: [
      {
        label: 'Chatwork',
        labelKey: 'chatwork',
        path: '/chatwork',
        // PR-30 (#270): Chatwork 公式 RGB symbol (logo_chatwork.zip 由来)。
        // light: `#13202f` dark navy + 赤、dark: `#e1e3e6` 薄灰 + 赤 (dark navy が
        // 黒背景に埋もれるための公式 invert 版)。
        iconImage: { light: chatworkColorLight, dark: chatworkColorDark },
        // PR-34 (#274): 4-block filled 公式アイコンが internal stroke icon より
        // 強く見えすぎるため、layout box は同じまま視覚サイズを scale-90 に縮小し
        // opacity-95 で controlled muted (Mgr prescription, brand 形状は無変更)。
        iconScale: 'scale-90',
        iconOpacity: 'opacity-95',
        permission: 'chatwork',
      },
      {
        label: 'LINE',
        labelKey: 'line',
        path: '/line',
        // PR-30 (#270): LINE 公式 Brand icon PNG (LINE_Brand_icon.zip 由来)。
        // brand カラーが緑単色のため light / dark 両方で視認可能 (single asset)。
        iconImage: { light: lineBrandIcon },
        // PR-34 (#274): Chatwork と同じく公式 filled アイコンを sidebar 用に控えめ化
        iconScale: 'scale-90',
        iconOpacity: 'opacity-95',
        permission: 'line',
      },
    ],
  },
  {
    header: '設定',
    headerKey: 'settings',
    // sidebar-color-system-finalization: 正本画像に合わせ indigo / periwinkle で統一
    // (書類・資料の violet、管理の slate とは明確に別色にする)。
    accent: 'indigo',
    items: [
      {
        // steering 463: 旧「管理 > 組織設定」(/admin/org) と旧「設定」(/settings) の 3 タブを
        // 6 タブへ集約した canonical ページ。requirements 確定 IA の順 (組織設定が先頭)。
        label: '組織設定',
        labelKey: 'orgSettings',
        path: '/settings/org',
        // PR-32 (#272): AceCollect 専用 SVG (settings.svg)
        iconSvg: settingsSvg,
        // PR-34 (#274): cog teeth が thin stroke で視覚的に弱く見えるため拡大
        iconScale: 'scale-110',
        // 個人の AI 連携は全職員に表示。組織の設定・連携はページ内で権限判定する。
        homePermissionAnyArea: ['orgSettings', 'integrations', 'chatwork', 'line'],
      },
      {
        // steering 463: 「書類・資料」から「設定」配下へ移動 (route / permission は不変)。
        // 旧「マスタ管理」の書類カテゴリ / 追加可能書類もこのページのタブへ移送済み。
        label: '書類マスタ',
        labelKey: 'docMaster',
        path: '/doc-master',
        // PR-32 (#272): AceCollect 専用 SVG (document-master.svg)
        iconSvg: documentMasterSvg,
        permission: 'collections',
      },
      {
        // 債権者マスタ: 実体は以前から存在したが、到達経路が借入・債権者モーダル内の
        // インライン toggle だけで「登録場所が分からない」状態だった。書類マスタと同じ
        // 「設定」配下のマスタとして導線を出す (route / API / RBAC は新設しない)。
        label: '債権者マスタ',
        labelKey: 'creditorMaster',
        path: '/creditor-master',
        iconSvg: creditorMasterSvg,
        permission: 'collections',
        // マスタの登録・編集・削除は collections:write が要る。モーダル内の既存導線
        // (canWrite('collections')) と**同じ条件**にし、閲覧のみのロールには出さない。
        permissionActions: ['write'],
      },
      {
        // sidebar-navigation-redesign: 「メッセージ設定」は文面テンプレートの *設定* であり、
        // 日常運用の会話画面 (メッセージ対応) や外部サービス連携 (Chatwork / LINE) とは
        // 役割が違う。コミュニケーション配下から「設定」配下へ移動した (route /messages は据置)。
        // steering 463: 確定 IA の順に合わせ末尾へ。機能・route・permission は不変。
        label: 'メッセージ設定',
        labelKey: 'messageSettings',
        path: '/messages',
        // PR-32 (#272): AceCollect 専用 SVG (message-settings.svg)
        iconSvg: messageSettingsSvg,
        // ADR-101: 中身の API (/admin/notification-preferences) と同じ orgSettings で判定する。
        // 以前は collections で出しており、member / viewer はメニューが見えるのに中身を読めなかった。
        permission: 'orgSettings',
      },
    ],
  },
  {
    // 旧「設定」セクション配下のアコーディオン (/admin + children) から独立セクションへ昇格。
    // 組織設定 / ユーザー管理 / 監査 をフラットに直接ナビゲートさせる。
    // RBAC は現行挙動維持のため 3 項目とも orgSettings でゲート。
    // (route guard App.tsx の anyArea=['orgSettings','userMgmt'] との不一致は今回 scope 外 / follow-up)
    // アイコンは 3 項目で識別できるよう「形状」を分ける (旧: 全て adminSvg の同一盾 → 盾の重複解消)。
    //
    // admin-settings-information-architecture: 旧「運用」(/admin/operations、通知設定 / 監査ログ /
    // セキュリティの 3 タブ) を廃止し、通知設定・セキュリティは「組織設定」のタブへ統合、
    // 監査ログは「監査」(/admin/audit) の主画面へ移した。旧 URL は AdminPage 側で
    // /admin/audit へ replace リダイレクトするため、bookmark / 外部リンクは 404 にならない。
    header: '管理',
    headerKey: 'admin',
    // sidebar-color-system-finalization: 正本画像に合わせ落ち着いた cool な slate blue-gray で統一。
    // 黄 / gold / orange は使わない (書類・資料の violet、設定の indigo とも別色)。
    accent: 'slate',
    items: [
      {
        label: 'ユーザー管理',
        labelKey: 'users',
        path: '/admin/users',
        // ID カード + 人物アイコン。users / ID 管理 を表す (盾は使わない)
        iconSvg: adminUsersSvg,
        // ユーザー一覧またはロール一覧を読める人を案内し、各タブは個別権限で絞る。
        permissionAnyArea: ['userMgmt', 'rbacSettings'],
      },
      {
        label: '監査',
        labelKey: 'audit',
        path: '/admin/audit',
        // 書類 + チェックアイコン。監査 (操作ログの点検) を表す (盾・レンチは使わない)
        iconSvg: adminAuditSvg,
        permission: 'orgSettings',
      },
    ],
  },
];

// ==============================
// Sidebar コンポーネント
// ==============================

/**
 * #406 UI-P2: nav item の active 判定 (pure・単体 test 可能)。
 * - path '/' はホーム完全一致。
 * - exact=true は完全一致のみ (別 item path の prefix になる path の二重 active を防ぐ)。
 * - それ以外は prefix 一致 (子ルート上でも親 nav を active に保つ従来挙動)。
 */
export function matchNavActive(pathname: string, path: string, exact?: boolean): boolean {
  if (path === '/') return pathname === '/';
  if (exact) return pathname === path;
  return pathname.startsWith(path);
}

interface SidebarProps {
  /** サイドバーの折りたたみ状態 */
  collapsed: boolean;
}

export default function Sidebar({ collapsed }: SidebarProps) {
  // 表示言語別の見出し・項目名 (admin-english-toggle-20260924 / ADR-081)
  const t = useT(shellNs);
  const location = useLocation();
  const navigate = useNavigate();
  const { canRead, canWrite, canExecute } = usePermission();
  const { isEnabled } = useFeatureGate();
  const [adminExpanded, setAdminExpanded] = useState(false);

  /** パスが現在のルートにマッチするか (判定本体は module-level matchNavActive) */
  const isActive = useCallback(
    (path: string, exact?: boolean) => matchNavActive(location.pathname, path, exact),
    [location.pathname],
  );

  /** 表示するセクションをフィルタリング (ops surface は専用メニューのみ) */
  const visibleSections = useMemo(() => {
    const sections = IS_OPS_SURFACE ? OPS_MENU_SECTIONS : MENU_SECTIONS;
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          canAccessNavigationItem(item, (area, action) =>
            action === 'write'
              ? canWrite(area)
              : action === 'execute'
                ? canExecute(area)
                : canRead(area),
          ),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [canRead, canWrite, canExecute]);

  return (
    <aside
      className={`flex h-full flex-col border-r border-gray-200 bg-white transition-all duration-200 dark:border-gray-700 dark:bg-gray-800 ${
        collapsed ? 'w-14' : 'w-56'
      }`}
    >
      {/* ナビゲーションメニュー */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {visibleSections.map((section, sectionIdx) => {
          // sidebar-navigation-redesign: セクション単位でアクセントトークンを引く。
          // 既定は brand、'入金管理' のみ emerald。
          const accent = SECTION_ACCENTS[section.accent ?? 'brand'];

          return (
            <div
              key={section.header ?? `section-${sectionIdx}`}
              // セクション区切り: カード化・背景ブロック化はせず、細い境界線 + 余白だけで
              // グループの切れ目を出す (先頭のダッシュボードには境界を付けない)。
              className={
                sectionIdx === 0
                  ? ''
                  : 'mt-2 border-t border-gray-200/80 pt-2 dark:border-gray-700/70'
              }
            >
              {/* セクションヘッダー (collapsed 時は非表示) */}
              {section.header && !collapsed && (
                <div className="flex items-center gap-2 px-3 pb-1.5 pt-1">
                  {/* アクセントのミニレール。グループの起点を示す小さな指標で、囲みではない */}
                  <span
                    aria-hidden="true"
                    className={`h-3 w-0.5 shrink-0 rounded-full ${accent.rail}`}
                  />
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-widest ${accent.header}`}
                  >
                    {section.headerKey ? t(`nav.sections.${section.headerKey}`) : section.header}
                  </span>
                </div>
              )}

              {/* メニュー項目 */}
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const itemLabel = t(`nav.items.${item.labelKey}`);
                  const active = isActive(item.path, item.exact);
                  const isBeta = item.betaFeature && !isEnabled(item.betaFeature);
                  const hasChildren = item.children && item.children.length > 0;

                  return (
                    <li key={item.path}>
                      <button
                        type="button"
                        onClick={() => {
                          if (isBeta) return;
                          if (hasChildren && !collapsed) {
                            setAdminExpanded((prev) => !prev);
                          } else {
                            navigate(item.path);
                          }
                        }}
                        onPointerEnter={() => {
                          if (!isBeta && !hasChildren) preloadNavigationPage(item.path);
                        }}
                        onFocus={() => {
                          if (!isBeta && !hasChildren) preloadNavigationPage(item.path);
                        }}
                        onPointerDown={() => {
                          if (!isBeta && !hasChildren) preloadNavigationPage(item.path);
                        }}
                        disabled={!!isBeta}
                        className={`group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                          active
                            ? accent.activeRow
                            : isBeta
                              ? 'cursor-not-allowed text-gray-400 dark:text-gray-600'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700/50 dark:hover:text-gray-100'
                        } ${collapsed ? 'justify-center' : ''}`}
                        title={collapsed ? itemLabel : undefined}
                        aria-current={active ? 'page' : undefined}
                      >
                        {/* active の左インジケーター。行を囲まず、選択位置だけを示す */}
                        {active && (
                          <span
                            aria-hidden="true"
                            className={`absolute inset-y-1.5 left-0 w-0.5 rounded-r-full ${accent.indicator}`}
                          />
                        )}
                        {/* アイコン:
                          - 優先 1: AceCollect 専用 SVG (PR-32 / PR-33 / 内部機能 13 種)
                            ZIP 内 SVG が `color="#XXXXXX"` 属性 (designer brand カラー)
                            を持ち `stroke="currentColor"` でそれを参照する設計。
                            CSS 仕様上 SVG element の presentation attribute は parent
                            inheritance より優先されるため、wrapper の text-color は
                            ここで「上書きしない」(Mgr 指示 推奨A)。active / hover は
                            row 背景・label 色で表現し、icon 自体は brand カラーを保つ。
                          - 優先 2: 公式 brand img (PR-30 / Chatwork & LINE)
                          - 優先 3: 旧 Heroicons SVG path (fallback、現状未使用)
                          PR-34 (#274): viewBox 内描画密度の不揃いを補正する
                          `item.iconScale` (例 scale-95 / scale-110) と、公式アイコンを
                          internal に馴染ませる `item.iconOpacity` (例 opacity-95) を
                          wrapper / img に適用。layout box (h-5 w-5) は維持。Beta は
                          opacity-40 を優先 (iconOpacity を上書き)。 */}
                        {item.iconSvg ? (
                          <span
                            aria-hidden="true"
                            className={`block h-5 w-5 shrink-0 [&_svg]:h-full [&_svg]:w-full ${
                              item.iconScale ?? ''
                            } ${isBeta ? 'opacity-40' : (item.iconOpacity ?? '')}`}
                            dangerouslySetInnerHTML={{ __html: item.iconSvg }}
                          />
                        ) : item.iconImage ? (
                          <>
                            <img
                              src={item.iconImage.light}
                              alt=""
                              aria-hidden="true"
                              className={`h-5 w-5 shrink-0 object-contain ${
                                item.iconImage.dark ? 'block dark:hidden' : ''
                              } ${item.iconScale ?? ''} ${isBeta ? 'opacity-40' : (item.iconOpacity ?? '')}`}
                            />
                            {item.iconImage.dark && (
                              <img
                                src={item.iconImage.dark}
                                alt=""
                                aria-hidden="true"
                                className={`hidden h-5 w-5 shrink-0 object-contain dark:block ${
                                  item.iconScale ?? ''
                                } ${isBeta ? 'opacity-40' : (item.iconOpacity ?? '')}`}
                              />
                            )}
                          </>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className={`h-5 w-5 shrink-0 ${
                              active
                                ? accent.iconActive
                                : isBeta
                                  ? 'text-gray-400 dark:text-gray-700'
                                  : accent.iconIdle
                            }`}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d={item.iconPath} />
                          </svg>
                        )}

                        {/* ラベル */}
                        {!collapsed && <span className="flex-1 text-left">{itemLabel}</span>}

                        {/* 未有効バッジ (管理者設定で有効化が必要) */}
                        {!collapsed && isBeta && (
                          <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800 dark:text-gray-600">
                            {t('nav.notEnabled')}
                          </span>
                        )}

                        {/* アコーディオンシェブロン (管理メニュー) */}
                        {!collapsed && hasChildren && !isBeta && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className={`h-4 w-4 transition-transform duration-200 ${
                              adminExpanded ? 'rotate-180' : ''
                            }`}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m19.5 8.25-7.5 7.5-7.5-7.5"
                            />
                          </svg>
                        )}
                      </button>

                      {/* サブメニュー (管理アコーディオン) */}
                      {!collapsed && hasChildren && adminExpanded && (
                        <ul className="ml-6 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3 dark:border-gray-700">
                          {item.children?.map((child) => {
                            const childActive = isActive(child.path);
                            return (
                              <li key={child.path}>
                                <button
                                  type="button"
                                  onPointerEnter={() => preloadNavigationPage(child.path)}
                                  onFocus={() => preloadNavigationPage(child.path)}
                                  onPointerDown={() => preloadNavigationPage(child.path)}
                                  onClick={() => navigate(child.path)}
                                  className={`w-full rounded-lg px-3 py-1.5 text-left text-sm transition-colors ${
                                    childActive
                                      ? 'text-brand-400'
                                      : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                                  }`}
                                >
                                  {child.labelKey ? t(`nav.items.${child.labelKey}`) : child.label}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
      {/* 開発者クレジット — サイドバー最下部。主導線より控えめ。collapsed 時は非表示。 */}
      {!collapsed && (
        <div className="shrink-0 px-4 py-3 text-[11px] font-normal leading-[1.4] text-gray-400 dark:text-gray-500">
          Developed by Erika Nariyama
        </div>
      )}
    </aside>
  );
}
